import React, { useState, useEffect, Dispatch, SetStateAction, useRef, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { Transition } from "react-transition-group";
import { toast } from "react-toastify";
import { selectSignedIn } from "reducers/auth";
import { HeaderAction, allPermissions, homePermissionsRequested } from "App";
import instance from "api";
import { endpoints } from "config";
import ToastMessage, { toastConfig } from "helpers/ToastMessage/ToastMessage";
import { Spinner } from "components/Spinner/Spinner";
import { WorkflowNotificationItem } from "./customComponents/NotificationItem";
import { useGetPermissions } from "helpers/useGetPermissions";
import {
  resetNotificationItems,
  selectNotificationItems,
  selectNumberOfNotifications,
  updateNumberOfNotifications,
} from "reducers/workflowNotifications";
import { RightPanel } from "components/rightPanel/RightPanel";
import WorkflowNotification from "pages/workflowNotifications/WorkflowNotification";
import { IoMdNotifications } from "react-icons/io";

type NotificationIconProps = {
  setHeaderActions: React.Dispatch<React.SetStateAction<HeaderAction | null>>;
  headerActions: HeaderAction | null;
};

const NotificationIcon: React.FC<NotificationIconProps> = ({ ...props }) => {
  const isSignedIn = useSelector(selectSignedIn);
  const accessToken = localStorage.getItem("AT");
  const [iconOptionsActive, setIconOptionsActive] = useState(false);
  const [notifications, setNotifications] = useState<any>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [errors, setErrors] = useState<any>(null);
  const [clearFilters, setClearFilters] = useState<boolean>(false);
  const dispatch = useDispatch();
  const numberOfNotifications = useSelector(selectNumberOfNotifications);

  const permissions = useGetPermissions(allPermissions, "/home");

  const notificationListAccess = permissions.get("notifications-list");

  const handleIconClick = (initial: boolean = false) => {
    if (!isSignedIn || !accessToken) return;
    if (!initial) {
      setIconOptionsActive(!iconOptionsActive);

      props.setHeaderActions(props.headerActions === "Notification" ? null : "Notification");
      if (props.headerActions === "Notification" || iconOptionsActive) return;
    }
    if (!notificationListAccess) return;
    setLoading(true);
    setErrors("");
    instance
      .get(
        endpoints.home.listWorkflowNotifications,
        clearFilters ? { params: { bClearFilters: true } } : {}
      )
      .then((res) => {
        setNotifications(res.data.details);
        setClearFilters(false);
        dispatch(updateNumberOfNotifications(res.data.details.resultsCount ?? 0));
      })
      .catch((err) => {
        setErrors(err);
      })
      .finally(() => setLoading(false));
  };
  const setOpen = () => {
    setIconOptionsActive(true);

    props.setHeaderActions("Notification");
  };
  if (!isSignedIn) return null;

  useEffect(() => {
    if (accessToken) {
      if (
        (notificationListAccess && numberOfNotifications === null && isSignedIn) ||
        (clearFilters && isSignedIn)
      ) {
        handleIconClick(true);
        if (clearFilters) {
          setOpen();
        }
      }
    }
  }, [permissions, clearFilters, isSignedIn]);

  const dropdownRef = useRef<any>(null);
  const handleClickOutside = useCallback(
    (event: any) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIconOptionsActive(false);

        if (props.headerActions === "Notification") props.setHeaderActions(null);
      }
    },
    [props.headerActions]
  );
  useEffect(() => {
    if (props.headerActions !== "Notification") return;
    document.addEventListener("click", handleClickOutside);
    return () => {
      if (props.headerActions !== "Notification") return;
      document.removeEventListener("click", handleClickOutside);
    };
  }, [props.headerActions]);

  const detectClickOutside = () => {
    setIconOptionsActive(false);
    props.setHeaderActions(null);
  };

  const homePermissions = useGetPermissions(homePermissionsRequested, "/home");

  const workflowNotifications = useSelector(selectNotificationItems);
  const viewNotificationOnRightPanel =
    homePermissions.has("notifications-view") &&
    homePermissions.get("notifications-view") &&
    !!workflowNotifications;

  return (
    <div className="AccountIconContainer" ref={dropdownRef}>
      <div className="AccountIcon" onClick={() => handleIconClick()}>
        {IoMdNotifications({ size: 25 })}
        {/* {notifications?.length > 0 && <span>{notifications?.length}</span>} */}
        {typeof numberOfNotifications === "number" && numberOfNotifications > 0 && (
          <span className="NumOfNotifications">{numberOfNotifications}</span>
        )}
      </div>
      <Transition
        in={props.headerActions === "Notification"}
        timeout={50}
        mountOnEnter
        unmountOnExit
      >
        <div className="Overlay">
          <WorkflowNotificationsMenu
            notifications={notifications.listData}
            showClearFilter={
              notifications.filters &&
              typeof notifications.filters === "string" &&
              notifications.filters !== ""
            }
            loading={loading}
            errors={errors}
            detectClickOutside={detectClickOutside}
            access={notificationListAccess}
            setClearFilters={setClearFilters}
            ref={dropdownRef}
          />
          {/* </ClickOutsideDetector> */}
        </div>
      </Transition>

      {viewNotificationOnRightPanel && (
        <RightPanel
          title={`${workflowNotifications[0].bCommunication ? "View communication" : "View workflow"}`}
          rightPanelContent={viewNotificationOnRightPanel}
          closePanel={() => {
            dispatch(resetNotificationItems());
          }}
          largeRightPanel={true}
        >
          {/* <StartNewWorkflow
            actionType={"view"}
            selectedData={workflowNotifications}
            onDone={() => dispatch(resetNotificationItems())}
          /> */}
          <WorkflowNotification
            onDone={() => dispatch(resetNotificationItems())}
            selectedData={workflowNotifications ?? []}
            actionType="view"
            idFromRowOption={null}
          />
        </RightPanel>
      )}
    </div>
  );
};

export { NotificationIcon };

type WorkflowNotificationsMenuProps = {
  detectClickOutside?(): void;
  notifications: any[];
  loading: boolean;
  errors: any;
  showClearFilter: boolean;
  setClearFilters: React.Dispatch<SetStateAction<boolean>>;
  access?: boolean;
  ref: any;
};

const WorkflowNotificationsMenu: React.FC<WorkflowNotificationsMenuProps> = ({
  detectClickOutside,
  errors,
  loading,
  notifications,
  access,
  showClearFilter,
  setClearFilters,
  ref,
}) => {
  const dispatch = useDispatch();
  const handleSignOutClick = () => {
    toast.dismiss();

    detectClickOutside && detectClickOutside();
  };

  return (
    <div className="AccountIconOptionsContainer NotificationsMenu">
      <div
        className={`AccountIconOptions NotificationOptions ${
          errors || notifications?.length === 0 ? "FailedtoFetch" : ""
        }`}
      >
        {showClearFilter && (
          <div className="ClearFilter" onClick={() => setClearFilters(true)}>
            Clear Filters
          </div>
        )}
        {!access ? (
          <>You may not have access permissions</>
        ) : errors && access ? (
          <>Failed to load notifications</>
        ) : loading ? (
          <Spinner positionAbsolute />
        ) : notifications?.length === 0 ? (
          <>No workflow notifications</>
        ) : (
          <>
            <div className="SectionHeader">
              <h4 style={{ marginBottom: "0" }}>WORKFLOW NOTIFICATIONS</h4>
              <Link to="/workflownotifications">View all workflow notifications</Link>
            </div>
            <div
              className={`WorkflowNotificationMenuList ${
                notifications?.length > 0 ? "" : "NoNotificationItems"
              }`}
            >
              {notifications?.length === 0 ? (
                <div style={{ textAlign: "center" }}>No workflow notifications</div>
              ) : (
                notifications?.map((n, _i) => {
                  return <WorkflowNotificationItem key={_i} notification={n} />;
                })
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

type ClickOutsideDetectorProps = {
  onClickOutside?(): void;
};

const ClickOutsideDetector: React.FC<ClickOutsideDetectorProps> = ({
  onClickOutside,
  children,
}) => {
  return (
    <div>
      <div className="ClickOutsideDetector" onClick={onClickOutside} />
      {children}
    </div>
  );
};
